import { type ButtonHTMLAttributes, type ReactNode, forwardRef } from "react";
import { cn } from "@/shared/cn";

type ButtonVariant =
  | "primary"
  | "brand"
  | "secondary"
  | "ghost"
  | "danger"
  | "default"
  | "xs"
  | "outline";

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: "md" | "sm";
  leadingIcon?: ReactNode;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = "default", size = "md", leadingIcon, className, children, type = "button", ...rest },
  ref,
) {
  const solid = variant in SOLID_CLASSES;
  return (
    <button
      ref={ref}
      type={type}
      className={cn(
        solid ? BUTTON_BASE_CLASS : null,
        solid ? SIZE_CLASSES[size] : null,
        VARIANT_CLASSES[variant],
        "font-(family-name:--font-display) font-medium",
        className,
      )}
      {...rest}
    >
      {leadingIcon}
      {children != null && <span>{children}</span>}
    </button>
  );
});

export const BUTTON_BASE_CLASS = cn(
  "inline-flex shrink-0 cursor-pointer items-center justify-center gap-[6px] whitespace-nowrap",
  "rounded-[12px] border leading-none transition-[background,border-color,color,opacity]",
  "duration-(--duration-fast) [&_svg]:size-[16px] [&_svg]:shrink-0",
  "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-(--brand)",
  "disabled:cursor-default disabled:opacity-45",
);

const SIZE_CLASSES = {
  md: "h-[38px] px-[16px] text-sm",
  sm: "h-[32px] px-[10px] text-sm",
} as const;

const SOLID_CLASSES = {
  primary: "border-(--ink) bg-(--ink) text-(--ground) hover:opacity-90 disabled:hover:opacity-45",
  brand:
    "border-(--brand) bg-(--brand) text-(--on-brand) shadow-[0_6px_18px_rgba(95,84,235,0.22)] hover:opacity-95",
  secondary:
    "border-(--line-strong) bg-(--paper) text-(--ink) hover:border-(--ink) disabled:hover:border-(--line-strong)",
  ghost: "border-transparent bg-transparent text-(--muted) hover:bg-(--fill) hover:text-(--ink)",
  danger:
    "border-[color-mix(in_srgb,var(--bad)_35%,transparent)] bg-transparent text-(--bad) hover:bg-[color-mix(in_srgb,var(--bad)_8%,transparent)]",
} as const;

const VARIANT_CLASSES: Record<ButtonVariant, string> = {
  ...SOLID_CLASSES,
  default:
    "inline-flex items-center gap-[8px] py-[4px] text-md leading-[1.2] tracking-[-0.01em] " +
    "text-[var(--color-text-dim)] [&_svg]:text-[var(--color-text-muted)] " +
    "transition-colors duration-[var(--duration-fast)] " +
    "hover:text-[var(--color-text)] hover:[&_svg]:text-[var(--color-text)] " +
    "disabled:opacity-40 disabled:hover:text-[var(--color-text-dim)] disabled:cursor-not-allowed",

  xs:
    "inline-flex items-center gap-[4px] text-xs leading-[1.2] tracking-[-0.01em] " +
    "text-[var(--color-text-dim)] [&_svg]:text-[var(--color-text-muted)] " +
    "transition-colors duration-[var(--duration-fast)] " +
    "hover:text-[var(--color-text)] hover:[&_svg]:text-[var(--color-text)] " +
    "disabled:opacity-40 disabled:cursor-not-allowed",

  outline:
    "inline-flex items-center gap-[8px] px-[8px] py-[8px] rounded-[var(--radius-md)] " +
    "border border-[var(--color-light-01)] bg-[var(--color-surface)] " +
    "text-md leading-[1.2] tracking-[-0.01em] " +
    "text-[var(--color-text-dim)] [&_svg]:text-[var(--color-text-muted)] " +
    "transition-colors duration-[var(--duration-fast)] " +
    "hover:border-[var(--color-dark-01)] hover:text-[var(--color-text)] hover:[&_svg]:text-[var(--color-text)] " +
    "disabled:opacity-40 disabled:hover:border-[var(--color-light-01)] disabled:cursor-not-allowed",
};
